import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import InputField from '../../components/ui/InputField';
import SelectField from '../../components/ui/SelectField';
import type { TenantCancellationRule } from '../../api/account/accountApiTypes';
import {
    CANCELLATION_PRESET_KEYS,
    MAX_CANCELLATION_TIERS,
    cancellationDraftInvalid,
    cancellationOnCategory,
    cancellationOnUseCase,
    cancellationShown,
    editedDraft,
    newTierId,
    presetDraft,
    presetOf,
    previewTiers,
} from './cancellationRules';
import type { CancellationDraft, CancellationMode, CancellationPreset } from './cancellationRules';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The customer-facing sentence for a rule (the one-line preview, and the inherited value). */
export const cancellationText = (
    t: Translate,
    mode: CancellationMode,
    tiers: ReadonlyArray<{ daysBefore: number, percent: number }>,
): string => {
    const key = (name: string) => `services.category_config.cancellation_${name}`;
    if (mode === 'none' || mode === 'inherit') return t(key('text_none'));
    if (mode === 'free' || tiers.length === 0) return t(key('text_free'));
    const sorted = [...tiers].sort((a, b) => b.daysBefore - a.daysBefore);
    const pieces = sorted.map((tier) => (tier.daysBefore === 0
        ? t(key('text_tier_start'), { percent: tier.percent })
        : t(key('text_tier'), { count: tier.daysBefore, percent: tier.percent })));
    return t(key('text_tiers'), { count: sorted[0]?.daysBefore ?? 0, tiers: pieces.join(', ') });
};

/** The inherited rule as the customer reads it — the "Standardwert" option's label. */
export const inheritedCancellationText = (t: Translate, rule: TenantCancellationRule | undefined): string => {
    if (!rule) return cancellationText(t, 'none', []);
    return cancellationText(t, rule.tiers.length === 0 ? 'free' : 'tiers', rule.tiers);
};

interface Props {
    value: CancellationDraft;
    onChange: (next: CancellationDraft) => void;
    /** The standard category has nothing to inherit from. */
    isStandard: boolean;
    /** What a further category inherits: the standard category's rule. */
    inherited: TenantCancellationRule | undefined;
    /** The "Standardwert" caption, shown while inheriting. */
    standardCaption: ReactNode;
    saving: boolean;
    /** Station units (SUP, rental, storage): customers do not see the rule yet. */
    notShownYet?: boolean;
}

/**
 * The cancellation section of `CategoryConfigForm` (M10, #3042). Four answers:
 * inherit (further categories), no rule — the AGB governs —, free until the
 * start, or fee tiers with a preset picker, editable rows and the AGB tick.
 * A one-line preview shows the sentence customers will read.
 */
const CancellationField = ({
    value, onChange, isStandard, inherited, standardCaption, saving, notShownYet,
}: Props) => {
    const { t } = useTranslation();
    const label = (key: string) => t(`services.category_config.cancellation_${key}`);
    const inheritText = inheritedCancellationText(t, inherited);
    const invalid = cancellationDraftInvalid(value);
    const preset = presetOf(value);
    const withRule = value.mode === 'free' || value.mode === 'tiers';

    const setMode = (mode: string) => {
        const next = mode as CancellationMode;
        // Tiers start from the halls preset rather than an empty table: an
        // edit, not a re-entry. Nothing is saved until the tenant ticks and saves.
        if (next === 'tiers' && value.tiers.length === 0) {
            onChange(presetDraft('halls', { ...value, mode: next }));
            return;
        }
        onChange(editedDraft(value, { mode: next }));
    };
    // Every change to the rule goes through `editedDraft`, which takes the AGB
    // tick back: a changed rule needs a fresh confirmation.
    const setTier = (index: number, field: 'daysBefore' | 'percent', raw: string) =>
        onChange(editedDraft(value, {
            tiers: value.tiers.map((tier, i) => (i === index ? { ...tier, [field]: raw } : tier)),
        }));

    const previewText = value.mode === 'inherit'
        ? inheritText
        : cancellationText(t, value.mode, previewTiers(value));

    return (
        <div className={'flex flex-col gap-2'}>
            <SelectField
                label={t('services.category_config.cancellation')}
                labelAbove={true}
                help={label('help')}
                value={value.mode}
                onChange={setMode}
                options={isStandard ? ['none', 'free', 'tiers'] : ['inherit', 'none', 'free', 'tiers']}
                optionLabels={{
                    inherit: t('services.category_config.inherit_value', { value: inheritText }),
                    none: label('none'),
                    free: label('free'),
                    tiers: label('tiers'),
                }}
                disabled={saving}
            />
            {value.mode === 'inherit' && standardCaption}
            {notShownYet && (
                <p className={'text-xs text-foreground/60'}>{label('not_shown_yet')}</p>
            )}

            {value.mode === 'tiers' && (
                <div className={'flex flex-col gap-2'}>
                    <SelectField
                        label={label('preset')}
                        labelAbove={true}
                        help={label('preset_help')}
                        value={preset}
                        onChange={(next) => {
                            if (next !== 'custom') onChange(presetDraft(next as CancellationPreset, value));
                        }}
                        options={preset === 'custom'
                            ? [...CANCELLATION_PRESET_KEYS, 'custom']
                            : [...CANCELLATION_PRESET_KEYS]}
                        optionLabels={Object.fromEntries(
                            [...CANCELLATION_PRESET_KEYS, 'custom'].map((key) => [key, label(`preset_${key}`)]),
                        )}
                        disabled={saving}
                    />
                    {value.tiers.map((tier, index) => (
                        <div key={tier.id} className={'grid grid-cols-[1fr_1fr_auto] gap-3 items-end'}>
                            <InputField
                                label={label('days_before')}
                                labelAbove={true}
                                value={tier.daysBefore}
                                onChange={(raw) => setTier(index, 'daysBefore', raw)}
                                disabled={saving}
                            />
                            <InputField
                                label={label('percent')}
                                labelAbove={true}
                                value={tier.percent}
                                onChange={(raw) => setTier(index, 'percent', raw)}
                                disabled={saving}
                            />
                            <button
                                type={'button'}
                                className={'pb-2 text-xs text-foreground/60 underline'}
                                onClick={() => onChange(editedDraft(value, { tiers: value.tiers.filter((_, i) => i !== index) }))}
                                disabled={saving}
                            >
                                {label('remove_tier')}
                            </button>
                        </div>
                    ))}
                    {value.tiers.length < MAX_CANCELLATION_TIERS && (
                        <button
                            type={'button'}
                            className={'self-start text-sm text-primary underline'}
                            onClick={() => onChange(editedDraft(value, {
                                tiers: [...value.tiers, { id: newTierId(), daysBefore: '', percent: '' }],
                            }))}
                            disabled={saving}
                        >
                            {label('add_tier')}
                        </button>
                    )}
                </div>
            )}

            {withRule && (
                <label className={'flex items-start gap-2 text-sm text-foreground'}>
                    <input
                        type={'checkbox'}
                        className={'mt-1'}
                        checked={value.agbConfirmed}
                        onChange={(e) => onChange({ ...value, agbConfirmed: e.target.checked, touched: true })}
                        disabled={saving}
                    />
                    <span>{label('agb')}</span>
                </label>
            )}

            <p className={'text-xs text-foreground/60'}>
                {t('services.category_config.cancellation_preview', { text: previewText })}
            </p>
            {invalid && <p className={'text-xs text-error'}>{label(invalid)}</p>}
        </div>
    );
};

/**
 * The section as the form places it: the field where the rule is
 * configurable, otherwise a line saying where it is set or that this use case
 * has none — an explained absence, not a missing field.
 */
export const CancellationSection = ({ useCase, ...props }: Props & { useCase: string }) => {
    const { t } = useTranslation();
    if (cancellationShown(useCase, props.isStandard)) {
        return <CancellationField {...props} notShownYet={!cancellationOnCategory(useCase)} />;
    }
    const reason = cancellationOnUseCase(useCase) ? 'cancellation_on_use_case' : 'cancellation_na';
    return <p className={'text-xs text-foreground/50'}>{t(`services.category_config.${reason}`)}</p>;
};

export default CancellationField;
